// ProcessingTable.jsx
import {
  formatDate,
  MappingStatus,
  MappingStyles,
} from "../../../../utils/utils";
import { crmAPI } from "../../../../services/AdminPortal/crmAPI";
import { toast } from "react-toastify";
import { useAuth } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function ProcessingTable({
  selectedAll,
  selectedApplicants,
  handleSelectAll,
  filteredApplicants,
  handleSelectApplicant, // Ensure this is the one used
  handleViewApplication,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Ensure filteredApplicants is an array
  // Parent component now sends paginated data, so we directly use filteredApplicants
  const applicantsToRender = Array.isArray(filteredApplicants)
    ? filteredApplicants
    : [];

  // Handle share application - generate and download PDF
  const handleShareApplication = async (applicationId) => {
    try {
      toast.info("Generating PDF... Please wait.");

      const result = await crmAPI.generateLoanApplicationPDF(applicationId);

      if (result.success) {
        toast.success(`PDF generated successfully! File: ${result.filename}`);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  console.log("Rendering ProcessingTable with applicants:", applicantsToRender);

  return (
<div className="bg-[#131313] rounded-lg border border-[#333333] w-full overflow-x-auto scrollbar-hide">
  <table className="min-w-full text-sm border-separate border-spacing-0">
    <thead className="bg-[#131313] text-white">
      <tr>
        <th className="p-4 text-center border-x border-[#333333] rounded-tl-lg">
          <input
            type="checkbox"
            className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 relative cursor-pointer
              before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
            checked={selectedAll}
            onChange={handleSelectAll}
          />
        </th>
        {[
          "DATE",
          "APPLICANT NAME",
          "LOAN AMOUNT",
          "CREDIT LIMIT",
          "PROCESSING TYPE",
          "BANK NAME",
          "PROCESSING TIME",
          "ACTIONS",
        ].map((heading, idx) => (
          <th
            key={idx}
            className={`p-4 text-center border-x border-[#333333] ${
              idx === 7 ? "rounded-tr-lg" : ""
            }`}
          >
            {heading}
          </th>
        ))}
      </tr>
    </thead>
    <tbody className="text-white bg-[#131313]">
      {applicantsToRender.length === 0 ? (
        <tr>
          <td colSpan="9" className="p-6 text-center text-gray-400">
            No rows found
          </td>
        </tr>
      ) : (
        applicantsToRender.map((applicant, index) => {
          const isLast = index === applicantsToRender.length - 1;
          return (
            <tr
              key={applicant._id || index}
              className={`border-b border-[#333333] ${
                isLast ? "last:rounded-b-lg" : ""
              }`}
            >
              <td className="p-4 text-center border-x border-[#333333]">
                <input
                  type="checkbox"
                  className="appearance-none h-5 w-5 border border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 relative cursor-pointer
                    before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                  checked={selectedApplicants.includes(applicant._id)}
                  onChange={() => handleSelectApplicant(applicant._id)}
                />
              </td>
              <td className="p-4 text-center border-x border-[#333333]">
                {formatDate(applicant.createdAt)}
              </td>
              <td className="p-4 text-center border-x border-[#333333]">
                <div className="flex flex-col items-center gap-1">
                  {applicant.user?.name || applicant.username || "N/A"}
                  {applicant.referredBy && (
                    <div className="relative inline-block group">
                      <span className="text-xs text-[#FFCA20] cursor-pointer">
                        Affiliate Application
                      </span>
                      <div className="absolute left-0 bottom-full z-10 hidden w-max p-2 mb-2 text-sm text-white bg-gray-800 border border-yellow-400 rounded-lg shadow-lg group-hover:block">
                        <p>Affiliate Name: {applicant.referredBy?.name}</p>
                        <p>Relation: {applicant.referredBy?.relation}</p>
                        <p>Affiliate Contact: {applicant.referredBy?.phone}</p>
                      </div>
                    </div>
                  )}
                </div>
              </td>
              <td className="p-4 text-center border-x border-[#333333]">
                <div>{applicant.principle}</div>
                <div className="text-sm text-gray-400">{applicant.loanType}</div>
              </td>
              <td className="p-4 text-center border-x border-[#333333]">
                <span
                  className={`px-3 py-1 rounded-full text-sm ${MappingStyles(
                    applicant.status
                  )}`}
                >
                  {MappingStatus(applicant.status)}
                </span>
              </td>
              <td className="p-4 text-center border-x border-[#333333]">
                {applicant.processingMethod}
              </td>
              <td className="p-4 text-center border-x border-[#333333]">
                <span>{applicant.bankName || "not assigned"}</span>
              </td>
              <td className="p-4 text-center border-x border-[#333333]">1 day</td>
              <td className="p-4 text-center border-x border-[#333333]">
                <button
                  className="text-[#FFCA20] mr-2 hover:text-[#FFCA20]"
                  onClick={() => handleViewApplication(applicant._id)}
                >
                  View
                </button>
                <button
                  className="text-[#FFCA20] ml-2 hover:text-[#FFCA20]"
                  onClick={() => handleShareApplication(applicant._id)}
                >
                  <svg
                    width="16"
                    height="15"
                    viewBox="0 0 16 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.75 5.78125H13.2188L9.25 2.34375C8.9375 2.0625 8.90625 1.59375 9.15625 1.28125C9.4375 0.96875 9.90625 0.9375 10.2188 1.1875L15.7188 5.9375C15.875 6.09375 16 6.3125 16 6.53125C16 6.71875 15.875 6.9375 15.7188 7.09375L10.2188 11.8438C10.0938 11.9688 9.90625 12.0312 9.75 12.0312C9.53125 12.0312 9.3125 11.9375 9.15625 11.75C8.90625 11.4375 8.9375 10.9688 9.25 10.6875L13.2188 7.28125H4.75C2.9375 7.28125 1.5 8.71875 1.5 10.5312V14.2812C1.5 14.6875 1.15625 15 0.75 15C0.3125 15 0 14.6875 0 14.2812V10.5312C0 7.90625 2.125 5.78125 4.75 5.78125Z"
                      fill="#FFCA20"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          );
        })
      )}
    </tbody>
  </table>
</div>

  );
}
